import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { deepMerge } from "@/lib/format";

/**
 * Manage load / save / save-as / duplicate of a tool calculation.
 * The URL ?id=<calcId> is the source of truth for an opened calculation.
 */
export function useCalculation(tool, makeDefault, defaultTitle) {
  const [params, setParams] = useSearchParams();
  const calcId = params.get("id");
  const [data, setDataRaw] = useState(() => makeDefault());
  const [meta, setMeta] = useState({ id: null, title: defaultTitle, permission: "owner", shares: [], owner_email: null });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const loadedId = useRef(null);

  const setData = useCallback((updater) => {
    setDataRaw((prev) => (typeof updater === "function" ? updater(prev) : updater));
    setDirty(true);
  }, []);

  useEffect(() => {
    if (!calcId) {
      if (loadedId.current) {
        loadedId.current = null;
        setDataRaw(makeDefault());
        setMeta({ id: null, title: defaultTitle, permission: "owner", shares: [], owner_email: null });
        setDirty(false);
      }
      return;
    }
    if (loadedId.current === calcId) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/calculations/${calcId}`)
      .then(({ data: calc }) => {
        if (cancelled) return;
        if (calc.tool !== tool) {
          toast.error("Perhitungan ini milik tool lain");
          return;
        }
        loadedId.current = calcId;
        setDataRaw(deepMerge(makeDefault(), calc.data || {}));
        setMeta({
          id: calc.id,
          title: calc.title,
          permission: calc.permission,
          shares: calc.shares || [],
          owner_email: calc.owner_email,
          updated_at: calc.updated_at,
          updated_by: calc.updated_by,
        });
        setDirty(false);
      })
      .catch((e) => {
        toast.error(errMsg(e, "Gagal memuat perhitungan"));
        setParams({}, { replace: true });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcId, tool]);

  const readOnly = meta.permission === "view";

  const applyCalc = (calc) => {
    loadedId.current = calc.id;
    setMeta({
      id: calc.id,
      title: calc.title,
      permission: calc.permission,
      shares: calc.shares || [],
      owner_email: calc.owner_email,
      updated_at: calc.updated_at,
      updated_by: calc.updated_by,
    });
    setParams({ id: calc.id }, { replace: true });
    setDirty(false);
  };

  const save = async (summary) => {
    if (!meta.title?.trim()) {
      toast.error("Isi judul perhitungan terlebih dahulu");
      return null;
    }
    setSaving(true);
    try {
      let calc;
      if (meta.id && (meta.permission === "owner" || meta.permission === "edit")) {
        ({ data: calc } = await api.put(`/calculations/${meta.id}`, { title: meta.title, data, summary }));
        toast.success("Perubahan tersimpan");
      } else {
        ({ data: calc } = await api.post("/calculations", { tool, title: meta.title, data, summary }));
        toast.success("Perhitungan tersimpan");
      }
      applyCalc(calc);
      return calc;
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAs = async (title, summary) => {
    setSaving(true);
    try {
      const { data: calc } = await api.post("/calculations", { tool, title, data, summary });
      toast.success("Disimpan sebagai perhitungan baru");
      applyCalc(calc);
      return calc;
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    if (!meta.id) return null;
    setSaving(true);
    try {
      const { data: calc } = await api.post(`/calculations/${meta.id}/duplicate`, {});
      toast.success("Diduplikat ke perhitungan milik Anda");
      setDataRaw(deepMerge(makeDefault(), calc.data || {}));
      applyCalc(calc);
      return calc;
    } catch (e) {
      toast.error(errMsg(e, "Gagal menduplikat"));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    loadedId.current = null;
    setDataRaw(makeDefault());
    setMeta({ id: null, title: defaultTitle, permission: "owner", shares: [], owner_email: null });
    setParams({}, { replace: true });
    setDirty(false);
  };

  const setTitle = (title) => {
    setMeta((m) => ({ ...m, title }));
    setDirty(true);
  };

  const setShares = (shares) => setMeta((m) => ({ ...m, shares }));

  return { data, setData, meta, setTitle, setShares, loading, saving, dirty, readOnly, save, saveAs, duplicate, reset };
}
